import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Layout
import Layout from "./components/layout/Layout";

// Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import CarbonCalculator from "./pages/CarbonCalculator";
import CompareDesigns from "./pages/CompareDesigns";
import AIAdvisor from "./pages/AIAdvisor";
import Reports from "./pages/Reports";
import AdminOverview from "./pages/AdminOverview";
import AdminCompanies from "./pages/AdminCompanies";
import AdminUsers from "./pages/AdminUsers";
import AdminActivity from "./pages/AdminActivity";

// Redirects authenticated users to the right landing page based on role
const HomeRedirect = () => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
};

// Blocks admins from architect-only routes (and vice-versa)
const RoleGate = ({ allow, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (allow === "admin" && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  if (allow === "user" && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />

          {/* Protected Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<HomeRedirect />} />

            {/* User-only routes */}
            <Route path="dashboard" element={<RoleGate allow="user"><Dashboard /></RoleGate>} />
            <Route path="projects" element={<RoleGate allow="user"><Projects /></RoleGate>} />
            <Route path="projects/:id" element={<RoleGate allow="user"><ProjectDetail /></RoleGate>} />
            <Route path="calculator" element={<RoleGate allow="user"><CarbonCalculator /></RoleGate>} />
            <Route path="compare" element={<RoleGate allow="user"><CompareDesigns /></RoleGate>} />
            <Route path="advisor" element={<RoleGate allow="user"><AIAdvisor /></RoleGate>} />
            <Route path="reports" element={<RoleGate allow="user"><Reports /></RoleGate>} />

            {/* Admin-only routes */}
            <Route path="admin" element={<RoleGate allow="admin"><AdminOverview /></RoleGate>} />
            <Route path="admin/companies" element={<RoleGate allow="admin"><AdminCompanies /></RoleGate>} />
            <Route path="admin/users" element={<RoleGate allow="admin"><AdminUsers /></RoleGate>} />
            <Route path="admin/activity" element={<RoleGate allow="admin"><AdminActivity /></RoleGate>} />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
